import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, LETTER_SPACING, LEVEL_LADDER, MOTION, NEUTRAL, SURFACE } from "@/theme/tokens";

/** Where a level stands. `Held` is one that won't come, because something is holding below it. */
export enum LevelLadderState {
	Done = "done",
	Current = "current",
	Upcoming = "upcoming",
	Held = "held",
}

export type LevelLadderLevel = {
	id: string;
	/** Short tag drawn inside the bar ("1 Light"). Already translated. */
	tag: string;
	/** The level's name under the track ("Allow calls"). */
	label: string;
	/** A line under the name: when it starts, "Done", "Holding". The current level's caption is drawn as a pill. */
	// DEFAULT: absent = no second line.
	caption?: string;
	/** The caption is the next thing to happen, so it takes the tone's ink. */
	// DEFAULT: false.
	captionIsNext?: boolean;
	/** The level is switched off: its name is struck through. */
	// DEFAULT: false.
	struck?: boolean;
	state: LevelLadderState;
	dataTestId?: string;
};

type Props = {
	levels: LevelLadderLevel[];
	/** How far along the whole ladder the track is filled, 0–1. */
	progress: number;
	/** The label on a dot at `progress` ("Today"); `null` draws no dot. */
	markerLabel: string | null;
	ariaLabel: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const {
	BAR_MIN_HEIGHT,
	BAR_STEP,
	BAR_RADIUS,
	TRACK_ROW,
	LINE,
	MARKER,
	MARKER_GAP,
	MARKER_RING,
	MARKER_RING_ALPHA,
	MARKER_LABEL_GAP,
	DONE_ALPHA,
	UPCOMING_ALPHA,
	HELD_ALPHA,
	HELD_EDGE_ALPHA,
	GROW_MS,
	GROW_STAGGER_MS,
} = LEVEL_LADDER;

const STATE_TEXT: Record<LevelLadderState, string> = {
	[LevelLadderState.Done]: "system.level_ladder.state.done",
	[LevelLadderState.Current]: "system.level_ladder.state.current",
	[LevelLadderState.Upcoming]: "system.level_ladder.state.upcoming",
	[LevelLadderState.Held]: "system.level_ladder.state.held",
};

const MICRO_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_MICRO,
	fontWeight: 700,
	letterSpacing: LETTER_SPACING.LABEL,
	textTransform: "uppercase",
	whiteSpace: "nowrap",
} as const;

const barSx = (state: LevelLadderState, index: number, tallest: number) => (theme: Theme) => {
	const { main } = theme.palette.primary;
	const fill = {
		[LevelLadderState.Done]: alpha(main, DONE_ALPHA),
		[LevelLadderState.Current]: main,
		[LevelLadderState.Upcoming]: alpha(main, UPCOMING_ALPHA),
		[LevelLadderState.Held]: alpha(main, HELD_ALPHA),
	}[state];
	// Only the current bar is solid enough for white ink; on the tinted done bar it fell to ~1.7:1.
	const solid = state === LevelLadderState.Current;
	return {
		"@keyframes levelLadderGrow": { from: { transform: "scaleY(0)" }, to: { transform: "scaleY(1)" } },
		width: "100%",
		height: BAR_MIN_HEIGHT + index * BAR_STEP,
		maxHeight: tallest,
		boxSizing: "border-box",
		display: "flex",
		alignItems: "flex-end",
		px: 1,
		pb: 0.375,
		borderRadius: `${BAR_RADIUS}px ${BAR_RADIUS}px 0 0`,
		backgroundColor: fill,
		border: state === LevelLadderState.Held ? `1px dashed ${alpha(main, HELD_EDGE_ALPHA)}` : "none",
		borderBottom: "none",
		color: solid ? SURFACE : theme.palette.primary.dark,
		transformOrigin: "bottom",
		animation: `levelLadderGrow ${GROW_MS}ms ${MOTION.EASE_OUT} ${index * GROW_STAGGER_MS}ms both`,
		"@media (prefers-reduced-motion: reduce)": { animation: "none" },
	};
};

const Caption: FC<{ level: LevelLadderLevel }> = ({ level }) => {
	if (level.caption === undefined) return null;
	if (level.state === LevelLadderState.Current) {
		return (
			<Box
				component="span"
				sx={{ ...MICRO_LABEL_SX, alignSelf: "flex-start", px: 0.75, py: 0.25, borderRadius: 0.5, backgroundColor: "primary.main", color: SURFACE }}
			>
				{level.caption}
			</Box>
		);
	}
	return (
		<Typography
			component="span"
			sx={{
				fontSize: FONT_SIZE.CAPTION,
				fontWeight: level.captionIsNext ? 600 : 400,
				color: level.captionIsNext ? "primary.dark" : INK.SECONDARY,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
		>
			{level.caption}
		</Typography>
	);
};

/** The track under the bars: a line filled to `progress`, and the marker dot with its label. */
const Track: FC<{ progress: number; markerLabel: string | null; top: number }> = ({ progress, markerLabel, top }) => {
	const at = `${Math.min(1, Math.max(0, progress)) * 100}%`;
	const move = {
		transition: `left ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}, width ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	};
	const lineTop = (MARKER - LINE) / 2;
	return (
		<Box aria-hidden sx={{ position: "absolute", insetInline: 0, top, height: TRACK_ROW, pointerEvents: "none" }}>
			<Box sx={{ position: "absolute", insetInline: 0, top: lineTop, height: LINE, backgroundColor: NEUTRAL[200] }} />
			<Box sx={{ position: "absolute", insetInlineStart: 0, top: lineTop, height: LINE, width: at, backgroundColor: "primary.main", ...move }} />
			{markerLabel !== null && (
				<Box
					sx={(theme) => ({
						position: "absolute",
						top: 0,
						insetInlineStart: at,
						width: MARKER,
						height: MARKER,
						marginInlineStart: `${-MARKER / 2}px`,
						borderRadius: "50%",
						backgroundColor: "primary.main",
						boxShadow: `0 0 0 ${MARKER_GAP}px ${SURFACE}, 0 0 0 ${MARKER_RING}px ${alpha(theme.palette.primary.main, MARKER_RING_ALPHA)}`,
						...move,
					})}
				>
					<Box
						component="span"
						sx={{ ...MICRO_LABEL_SX, position: "absolute", top: MARKER + MARKER_LABEL_GAP, left: "50%", transform: "translateX(-50%)", color: "primary.dark" }}
					>
						{markerLabel}
					</Box>
				</Box>
			)}
		</Box>
	);
};

/**
 * An **escalation ladder**: N ordered levels drawn as bars that rise level by level, so "higher" reads at a glance,
 * over a track filled to where things stand, with an optional marker ("Today"). Under the track, each level's name
 * and one caption line (when it starts, "Done", "Holding"); the current level's caption is a pill. Done bars are
 * tinted, the current one solid, upcoming ones pale, held ones dashed. A semantic `<ol>` with `aria-current="step"`;
 * each level also announces its state (and "off" when struck) as visually hidden text.
 * Bars grow in one after another, reduced-motion safe. For a pipeline of equal stages use `SystemStageTrack`.
 */
export const SystemLevelLadder: FC<Props> = ({ levels, progress, markerLabel, ariaLabel, dataTestId, sx }) => {
	const { t } = useTranslation();
	const tallest = BAR_MIN_HEIGHT + (levels.length - 1) * BAR_STEP;
	return (
		<Box
			component="ol"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={[
				{
					position: "relative",
					m: 0,
					p: 0,
					listStyle: "none",
					display: "grid",
					gridTemplateColumns: `repeat(${levels.length}, minmax(0, 1fr))`,
					columnGap: 0.75,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{levels.map((level, index) => (
				<Box
					component="li"
					key={level.id}
					data-testid={level.dataTestId}
					data-level-state={level.state}
					aria-current={level.state === LevelLadderState.Current ? "step" : undefined}
					sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
				>
					<Box sx={{ height: tallest, display: "flex", alignItems: "flex-end" }}>
						<Box sx={barSx(level.state, index, tallest)}>
							<Box
								component="span"
								sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								{level.tag}
							</Box>
						</Box>
					</Box>
					<Box sx={{ height: TRACK_ROW }} />
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.375, minWidth: 0 }}>
						<Typography
							component="span"
							sx={{
								fontSize: FONT_SIZE.CAPTION,
								fontWeight: 600,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								color: level.struck
									? INK.MUTED
									: level.state === LevelLadderState.Current
										? "primary.dark"
										: level.state === LevelLadderState.Done
											? INK.SECONDARY
											: INK.PRIMARY,
								textDecoration: level.struck ? "line-through" : "none",
							}}
						>
							{level.label}
						</Typography>
						<Caption level={level} />
						{/* The bar's fill and the strike say this visually; say it to assistive technology too. */}
						<Box component="span" sx={visuallyHidden}>
							{[t(STATE_TEXT[level.state]), ...(level.struck ? [t("system.level_ladder.off")] : [])].join(", ")}
						</Box>
					</Box>
				</Box>
			))}
			<Track progress={progress} markerLabel={markerLabel} top={tallest} />
		</Box>
	);
};
